// File Analysis IR types and Zod schemas (ARCHITECTURE.md §4.3, §6.2).
//
// FileAnalysisResult is the language-independent intermediate representation
// produced by every LanguageAnalyzer and consumed by all downstream stages.
// Downstream modules never see raw ASTs or parser-specific types — only this IR.
//
// As with src/types.ts, the inferred Zod types are the compile-time source of
// truth; the schemas provide runtime validation at trust boundaries (cache
// deserialization, plugin input).

import { z } from "zod";

import { GraphEdgeSchema } from "../types.js";

// ---------------------------------------------------------------------------
// SymbolKind
// ---------------------------------------------------------------------------

export const SYMBOL_KINDS = [
  "function",
  "class",
  "type",
  "variable",
  "interface",
  "enum",
  "module",
  "trait", // Rust
  "struct", // Rust, PHP
  "constant",
] as const;

export const SymbolKindSchema = z.enum(SYMBOL_KINDS);

export type SymbolKind = z.infer<typeof SymbolKindSchema>;

// ---------------------------------------------------------------------------
// SymbolInfo
// ---------------------------------------------------------------------------

export const SymbolInfoSchema = z.object({
  name: z.string(),
  kind: SymbolKindSchema,
  exported: z.boolean(),
  line: z.number(),
  /** File-unique symbol id (filePath#name form), used as the GraphNode id. */
  symbolId: z.string(),
  /** 1-based end line of the symbol's scope (for line-range extraction). */
  endLine: z.number(),
  /** Parent symbol id for nested symbols (e.g. a method inside a class). */
  parentSymbolId: z.string().optional(),
  /** Symbol signature (parameter / return-type info). */
  signature: z.string().optional(),
});

export type SymbolInfo = z.infer<typeof SymbolInfoSchema>;

// ---------------------------------------------------------------------------
// FileAnalysisMetadata
// ---------------------------------------------------------------------------

export const FileAnalysisMetadataSchema = z.object({
  // e.g. "typescript-compiler-api", "tree-sitter-rust"
  parserBackend: z.string(),
  parseTimeMs: z.number(),
  lineCount: z.number(),
});

export type FileAnalysisMetadata = z.infer<typeof FileAnalysisMetadataSchema>;

// ---------------------------------------------------------------------------
// FileAnalysisResult
// ---------------------------------------------------------------------------

export const FileAnalysisResultSchema = z.object({
  filePath: z.string(),
  // informational only — downstream MUST NOT branch on this
  language: z.string(),
  symbols: z.array(SymbolInfoSchema),
  // uses the same GraphEdge from §4.1
  edges: z.array(GraphEdgeSchema),
  metadata: FileAnalysisMetadataSchema,
});

export type FileAnalysisResult = z.infer<typeof FileAnalysisResultSchema>;

// ---------------------------------------------------------------------------
// AnalyzerCapability
// ---------------------------------------------------------------------------

export const ANALYZER_CAPABILITIES = [
  "symbols",
  "imports",
  "exports",
  "calls",
  "type_references",
] as const;

export const AnalyzerCapabilitySchema = z.enum(ANALYZER_CAPABILITIES);

export type AnalyzerCapability = z.infer<typeof AnalyzerCapabilitySchema>;

// ---------------------------------------------------------------------------
// LanguageAnalyzer
// ---------------------------------------------------------------------------

// LanguageAnalyzer carries a behavioural method (analyze), so it is expressed
// as a TypeScript interface rather than a Zod schema — functions are not
// meaningfully validatable at runtime. Implementations live in built-in/.
export interface LanguageAnalyzer {
  readonly language: string;
  readonly capabilities: AnalyzerCapability[];
  /**
   * File extensions this analyzer handles, e.g. [".ts", ".tsx"]. Used by
   * AnalyzerRegistry for extension-based resolution (ARCHITECTURE.md §6.4).
   * Optional: when omitted, the registry falls back to a language-name map.
   */
  readonly fileExtensions?: readonly string[];
  analyze(filePath: string, content: string): Promise<FileAnalysisResult>;
}
